// Enquiry-only meal plans (Corporate Meal Plans, Family Feast).
//
// These offerings are dynamic/customized and are NOT configured for automatic
// ordering. Their public cards and detail pages route to the contact enquiry
// form with the enquiry type preselected, and the team confirms requirements,
// menu, pricing and fulfilment manually. No paid order or Razorpay checkout
// is ever created from these paths.

export const ENQUIRY_ONLY_PLAN_SLUGS = ["corporate-wellness", "family-feast"] as const;

export type EnquiryOnlyPlanSlug = (typeof ENQUIRY_ONLY_PLAN_SLUGS)[number];

/** Customer-facing enquiry type labels stored in the enquiry's meal-interest field. */
export const ENQUIRY_TYPE_LABELS: Record<EnquiryOnlyPlanSlug, string> = {
  "corporate-wellness": "Corporate Meal Plan",
  "family-feast": "Family Feast",
};

/** Values accepted by the /contact?enquiry=... search param. */
export const ENQUIRY_PARAM_VALUES = [
  "corporate-meal-plan",
  "family-feast",
] as const;

export type EnquiryParamValue = (typeof ENQUIRY_PARAM_VALUES)[number];

const PARAM_TO_LABEL: Record<EnquiryParamValue, string> = {
  "corporate-meal-plan": ENQUIRY_TYPE_LABELS["corporate-wellness"],
  "family-feast": ENQUIRY_TYPE_LABELS["family-feast"],
};

/** Maps a plan slug to the /contact enquiry search-param value, or null. */
export function enquiryParamForPlanSlug(slug: string): EnquiryParamValue | null {
  if (slug === "corporate-wellness") return "corporate-meal-plan";
  if (slug === "family-feast") return "family-feast";
  return null;
}

/** Validates a raw ?enquiry= value; returns null for anything unrecognized. */
export function parseEnquiryParam(raw: unknown): EnquiryParamValue | null {
  return typeof raw === "string" &&
    (ENQUIRY_PARAM_VALUES as readonly string[]).includes(raw)
    ? (raw as EnquiryParamValue)
    : null;
}

/** Preselected meal-interest label for a validated enquiry param. */
export function enquiryLabelForParam(param: EnquiryParamValue): string {
  return PARAM_TO_LABEL[param];
}

/** True when the selected meal interest is one of the enquiry-only plan types. */
export function isPlanEnquiryType(mealInterest: string): boolean {
  return mealInterest === ENQUIRY_TYPE_LABELS["corporate-wellness"] ||
    mealInterest === ENQUIRY_TYPE_LABELS["family-feast"];
}

export type PlanEnquiryDetails = {
  peopleCount?: string;
  preferredDate?: string;
  preferredTime?: string;
  dietaryPreferences?: string;
};

/**
 * Composes the extra detail lines stored with a Corporate/Family Feast
 * enquiry. Blank fields are omitted so the saved message stays clean.
 */
export function composePlanEnquiryLines(details: PlanEnquiryDetails): string[] {
  const lines: string[] = [];
  const people = details.peopleCount?.trim();
  if (people) lines.push(`Number of people/meals: ${people}`);
  const date = details.preferredDate?.trim();
  if (date) lines.push(`Preferred date: ${date}`);
  const time = details.preferredTime?.trim();
  if (time) lines.push(`Preferred delivery time: ${time}`);
  const dietary = details.dietaryPreferences?.trim();
  if (dietary) lines.push(`Dietary preferences / special requirements: ${dietary}`);
  return lines;
}
